/**
 * Copyright (c) Qwoach LLC 2026. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */
import BaseObject from "@shared/BaseObject.js";
import DraftLineItem from "@shared/Invoice/DraftLineItem";
import {DataFilter} from "@qwoach/firebase-data-manager";
import {ensureArray} from "@/utils/helpers";
import LineItem from "@shared/Invoice/LineItem";
import {InvoiceStatus} from "@shared/Invoice/InvoiceStatus";
import {ServiceType} from "@shared/Quote/ServiceType";
import {classToClass} from "@qwoach/class-transformer-wrapper";
import {
	buildConsumedUsageGroupingKey,
	buildConsumedUsageGroupingKeyParts
} from "@tl/core/modules/commission/model/commission/ConsumedUsageGrouping";

const SESSION_DERIVED_FIELDS = Object.freeze([
	"sourceServiceId", "title", "serviceDateMs", "quantity", "quantityUnits", "conversionFactor",
	"quantityNumber", "rateType", "itemPriceCents",
])

export default class DraftLineItemManager extends BaseObject {

	dataManager = null

	constructor(dataManager) {
		super()
		this.dataManager = dataManager || null
	}

	async loadDraftLineItemsByClientId(clientId) {
		let dataFilter = new DataFilter()
			.where("clientId", "==", clientId)
		let pager = await this.dataManager.getDraftLineItemListPager(dataFilter)
		let draftLineItems = await pager.loadAll()
		return draftLineItems.filter(item => !item.isBillingSuspended)
	}

	normalizeSessionDraftChildren(lineItem) {
		if (lineItem?.serviceType !== ServiceType.SESSION) return
		if (!(lineItem.subLineItems || []).some(child => !child.sourceServiceId)) return
		LineItem.normalizeSessionDerivedSubItems(lineItem)
	}

	async syncDraftLineItemDb(lineItem, clientId, clientProfile = null) {
		this.normalizeSessionDraftChildren(lineItem)
		let draftLineItem = await this.dataManager.getDraftLineItem(lineItem.id)
		await this.updateScheduleSyncOverrides(lineItem, clientId, clientProfile, draftLineItem?.lineItem)
		if (!draftLineItem) {
			draftLineItem = new DraftLineItem({
				lineItem,
				clientId,
			})
		} else {
			draftLineItem.setLineItem(lineItem)
		}

		let shouldDelete = lineItem.getTotalPriceCents() === 0
		if (shouldDelete) {
			await this.deleteDraftLineItemDb(draftLineItem)
		} else {
			await this.dataManager.setDraftLineItem(draftLineItem)
		}

		return {
			draftLineItem,
			shouldDelete,
		}
	}

	async updateScheduleSyncOverrides(lineItem, clientId, clientProfile = null, previousLineItem = null) {
		if (!lineItem.sourceSessionEntryId) return
		let sessionEntry = await this.dataManager.getJournalEntry(lineItem.sourceSessionEntryId)
		let profile = clientProfile || await this.dataManager.getClientProfile(clientId)
		let generatedLineItem = LineItem.fromSessionEntry({
			sessionEntry,
			quoteItem: profile?.getCommissionDetails?.()?.getQuoteItem?.(),
			sourceServiceId: lineItem.sourceServiceId,
		})
		if (!generatedLineItem) return
		let previousOverrides = new Set(previousLineItem?.scheduleSyncOverrides || [])
		lineItem.scheduleSyncOverrides = SESSION_DERIVED_FIELDS
			.filter(field => previousOverrides.has(field) || lineItem[field] !== generatedLineItem[field])
	}

	async syncSessionDraftUsage(sessionEntry, clientProfile) {
		let draftLineItem = await this.dataManager.getDraftLineItem(sessionEntry.id)
		if (!draftLineItem) return null
		if (sessionEntry.isBillingDisconnected) {
			if (sessionEntry.isBillingReconciliationRequired || draftLineItem.isBillingSuspended) {
				if (!draftLineItem.isBillingSuspended) {
					draftLineItem.isBillingSuspended = true
					await this.dataManager.updateDraftLineItem(draftLineItem)
				}
			} else {
				await this.dataManager.deleteDraftLineItem(draftLineItem)
			}
			return null
		}
		let generatedLineItem = LineItem.fromSessionEntry({
			sessionEntry,
			quoteItem: clientProfile?.getCommissionDetails?.()?.getQuoteItem?.(),
			sourceServiceId: draftLineItem.lineItem?.sourceServiceId,
		})
		if (!generatedLineItem) return draftLineItem
		let lineItem = draftLineItem.lineItem
		let overrides = new Set(lineItem.scheduleSyncOverrides || [])
		let sourceServiceChanged = lineItem.sourceServiceId !== generatedLineItem.sourceServiceId
		let hasChanges = draftLineItem.isBillingSuspended
		draftLineItem.isBillingSuspended = false
		for (let field of SESSION_DERIVED_FIELDS) {
			if (overrides.has(field) || lineItem[field] === generatedLineItem[field]) continue
			lineItem[field] = generatedLineItem[field]
			hasChanges = true
		}
		if (sourceServiceChanged && !overrides.has("sourceServiceId")) {
			lineItem.subLineItems = generatedLineItem.subLineItems
			hasChanges = true
		}
		if (hasChanges) await this.dataManager.updateDraftLineItem(draftLineItem)
		return draftLineItem
	}

	async deleteDraftLineItemDb(draftLineItem) {
		if (!draftLineItem) {
			return false
		}
		let sourceSessionEntryId = draftLineItem.lineItem?.sourceSessionEntryId
		if (sourceSessionEntryId) {
			let sessionEntry = await this.dataManager.getJournalEntry(sourceSessionEntryId)
			if (sessionEntry && !sessionEntry.isBillingDisconnected) {
				await this.dataManager.updateJournalEntry({id: sourceSessionEntryId, isBillingDisconnected: true})
			}
		}
		await this.dataManager.deleteDraftLineItem(draftLineItem)
		return true
	}

	applyDraftLineItemChange(draftLineItems = [], {
		draftLineItem = null,
		shouldDelete = false,
	} = {}) {
		let nextDraftLineItems = [...ensureArray(draftLineItems)]
		if (!draftLineItem?.id) {
			return nextDraftLineItems
		}
		let itemIndex = nextDraftLineItems.findIndex(item => item?.id === draftLineItem.id)
		if (shouldDelete) {
			if (itemIndex < 0) {
				return nextDraftLineItems
			}
			nextDraftLineItems.splice(itemIndex, 1)
			return nextDraftLineItems
		}
		if (itemIndex < 0) {
			nextDraftLineItems.push(draftLineItem)
			return nextDraftLineItems
		}
		nextDraftLineItems.splice(itemIndex, 1, draftLineItem)
		return nextDraftLineItems
	}

	getDraftLineItemsBySourceServiceId(draftLineItems = [], sourceServiceId) {
		return ensureArray(draftLineItems)
			.filter(draftLineItem => draftLineItem?.lineItem?.sourceServiceId === sourceServiceId)
			.sort((itemA, itemB) => {
				let dateA = Number(itemA?.lineItem?.serviceDateMs) || 0
				let dateB = Number(itemB?.lineItem?.serviceDateMs) || 0
				if (dateA === dateB) {
					return String(itemB?.id || "").localeCompare(String(itemA?.id || ""))
				}
				return dateB - dateA
			})
	}

	findLatestDraftLineItemBySourceServiceId(draftLineItems = [], sourceServiceId) {
		let matchingDraftLineItems = this.getDraftLineItemsBySourceServiceId(draftLineItems, sourceServiceId)
		return matchingDraftLineItems[0] || null
	}

	sessionEntriesToDraftLineItems(clientProfile, sessionEntries = [], persistedDraftLineItems = []) {
		let commissionDetails = clientProfile.getCommissionDetails()
		let quoteItem = commissionDetails.getQuoteItem()
		let sessionDraftLineItemArray = []
		let persistedIds = new Set(ensureArray(persistedDraftLineItems).map(item => item?.id))

		for (let sessionEntry of ensureArray(sessionEntries)) {
			if (persistedIds.has(sessionEntry?.id)
				|| !sessionEntry?.startTimeMs
				|| sessionEntry?.isBillingDisconnected
				|| ![InvoiceStatus.NOT_SET, InvoiceStatus.DRAFT, null].includes(sessionEntry?.invoiceStatus)) {
				continue
			}
			let sessionLineItem = LineItem.fromSessionEntry({
				sessionEntry,
				quoteItem
			})
			if (!sessionLineItem) {
				continue
			}
			sessionDraftLineItemArray.push(new DraftLineItem({
				lineItem: sessionLineItem,
				clientId: clientProfile.id,
			}))
		}

		return sessionDraftLineItemArray
	}

	collapseDraftLineItemsByInvoicePeriod(draftLineItems = [], invoicePeriodKey) {
		let groupsByKey = this.groupDraftLineItemsByKey(draftLineItems, invoicePeriodKey)
		return Object.values(groupsByKey)
			.map(group => this.mergeDraftLineItemGroup(group, invoicePeriodKey))
	}

	groupDraftLineItemsByKey(draftLineItems = [], invoicePeriodKey) {
		let groups = {}
		for (let draftLineItem of ensureArray(draftLineItems)) {
			let groupKey = this.buildDraftLineItemGroupKey(draftLineItem, invoicePeriodKey)
			if (!groups[groupKey]) {
				groups[groupKey] = []
			}
			groups[groupKey].push(draftLineItem)
		}
		return groups
	}

	buildDraftLineItemGroupKey(draftLineItem, invoicePeriodKey) {
		let keyParts = [
			...this.buildDraftLineItemBaseKeyParts(draftLineItem, invoicePeriodKey),
			...this.buildDraftLineItemUsageTypeKeyParts(draftLineItem),
		]
		return buildConsumedUsageGroupingKey(keyParts)
	}

	mergeDraftLineItemGroup(group = [], invoicePeriodKey) {
		let firstDraftLineItem = group[0]
		if (!firstDraftLineItem) {
			return null
		}
		if (group.length === 1 && firstDraftLineItem.lineItem?.serviceType === ServiceType.SESSION) {
			return firstDraftLineItem
		}

		let mergedLineItem = classToClass(firstDraftLineItem.lineItem)
		let usageValueTotal = group.reduce((total, item) => total + (item?.lineItem?.getUsageValue?.() || 0), 0)
		let latestDateMs = Math.max(...group.map(item => Number(item?.lineItem?.serviceDateMs) || 0))
		let draftLineItemIds = [...new Set(group
			.map(item => item?.id || null)
			.filter(id => !!id))]

		mergedLineItem.id = this.buildGroupedLineItemId(firstDraftLineItem, invoicePeriodKey)
		mergedLineItem.serviceDateMs = latestDateMs || mergedLineItem.serviceDateMs || null
		mergedLineItem._draftLineItemIds = draftLineItemIds
		mergedLineItem.setUsageValue(usageValueTotal)

		return new DraftLineItem({
			lineItem: new LineItem(mergedLineItem),
			clientId: firstDraftLineItem.clientId,
		})
	}

	buildGroupedLineItemId(draftLineItem, invoicePeriodKey) {
		let keyParts = [
			"grouped",
			...this.buildDraftLineItemBaseKeyParts(draftLineItem, invoicePeriodKey),
			...this.buildDraftLineItemUsageTypeKeyParts(draftLineItem),
		]
		return buildConsumedUsageGroupingKey(keyParts)
	}

	buildDraftLineItemBaseKeyParts(draftLineItem, invoicePeriodKey) {
		let lineItem = draftLineItem?.lineItem || {}
		return [
			invoicePeriodKey,
			lineItem.serviceType === ServiceType.SESSION ? lineItem.id || "" : "",
			lineItem.title || "",
			lineItem.description || "",
			lineItem.serviceUsageType || "",
			lineItem.sourceServiceId || "",
			lineItem.serviceType || "",
		]
	}

	buildDraftLineItemUsageTypeKeyParts(draftLineItem) {
		let lineItem = draftLineItem?.lineItem || {}
		return buildConsumedUsageGroupingKeyParts(lineItem)
	}
}
